/**
 * ArrayField - Array item editor with add/remove capabilities
 *
 * Supports both expanded mode (list of items) and collapsed mode (JSON text).
 * Serializes as an actual array (not a JSON string).
 * Auto-populates empty rows for easy entry.
 * Supports complex object items with nested property forms.
 */

import { useState, useCallback, useMemo, useRef, useEffect, type ReactNode } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  mergeClasses,
  Label,
  Text,
  Button,
  Tooltip,
} from '@coreai/fluentui-react';
import {
  DeleteFilled,
  CodeFilled,
  TextFieldFilled,
  AddFilled,
  ChevronDownFilled,
  ChevronRightFilled,
} from '@fluentui/react-icons';
import {
  generateSegmentId,
  ValueSegmentType,
  isValueSegmentArray,
  segmentsToString,
} from '@/types/valueSegment';
import type { ValueSegment } from '@/types/valueSegment';
import { containsTemplateToken } from '@/utils/expressions';
import { ExpressionPreview } from './ExpressionPreview';
import { TokenizedInput } from './TokenizedInput';
import { BooleanField } from './BooleanField';
import { useFieldPreviewFocus } from './useFieldPreviewFocus';
import {
  fieldContainerStyle,
  fieldLabelRowStyle,
  fieldLabelStyle,
  fieldRequiredStyle,
} from './fieldStyles';

const useStyles = makeStyles({
  container: fieldContainerStyle,
  labelRow: fieldLabelRowStyle,
  label: fieldLabelStyle,
  required: fieldRequiredStyle,
  modeToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  // Vertical separator between FieldActionChips (left) and mode-toggle
  // buttons (right) in the labelRow. Visual hierarchy: action entry-points
  // come first, then a hairline, then the mode controls — mirrors the
  // pattern in the Sonner-style toolbars and the value-source tab strip.
  labelRowSeparator: {
    width: '1px',
    alignSelf: 'stretch',
    marginLeft: tokens.spacingHorizontalXXS,
    marginRight: tokens.spacingHorizontalXXS,
    backgroundColor: tokens.colorNeutralStroke2,
    '@media (forced-colors: active)': {
      backgroundColor: 'CanvasText',
    },
  },
  // Wrapper for item list + preview overlay positioning
  itemListWrapper: {
    position: 'relative',
  },
  itemList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  itemIndex: {
    minWidth: '24px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  itemInput: {
    flex: 1,
  },
  deleteButton: {
    minWidth: 'auto',
    height: '32px',
    padding: tokens.spacingHorizontalS,
  },
  deleteButtonHidden: {
    visibility: 'hidden',
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  // Wrapper for JSON mode input + preview overlay positioning
  jsonEditorWrapper: {
    position: 'relative',
  },
  // Slot for caller-provided end actions (e.g. FieldActionChips). Used
  // inside any of the bordered editor surfaces to anchor chips on the
  // right edge of the input rather than floating above the row.
  endActionsOverlay: {
    position: 'absolute',
    top: tokens.spacingVerticalXXS,
    right: tokens.spacingHorizontalSNudge,
    zIndex: 1,
    pointerEvents: 'auto',
  },
  endActionsInLabelRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  // Complex object item styles
  objectItemList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  objectItemCard: {
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    overflow: 'hidden',
  },
  objectItemHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  objectItemHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  objectItemIndex: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  objectItemSummary: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  objectItemContent: {
    padding: tokens.spacingHorizontalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  objectItemProperty: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  objectItemPropertyLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
  },
  objectItemPropertyRequired: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  addItemButton: {
    alignSelf: 'flex-start',
  },
  expandButton: {
    minWidth: 'auto',
    width: '24px',
    height: '24px',
    padding: 0,
  },
  emptyState: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    fontStyle: 'italic',
  },
});

/** Simple array item for primitive values */
interface ArrayItem {
  id: string;
  value: string;
}

/** Complex array item for object values */
interface ComplexArrayItem {
  id: string;
  values: Record<string, string>; // property name -> value
  isExpanded: boolean;
}

/** Property schema for complex items */
interface ItemPropertySchema {
  name: string;
  type?: string | undefined;
  description?: string | undefined;
  format?: string | undefined;
  'x-ms-summary'?: string | undefined;
}

/** Resolved item schema with properties */
interface ResolvedItemSchema {
  type: 'object';
  properties: Record<string, ItemPropertySchema>;
  required?: string[] | undefined;
}

export interface ArrayFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[] | unknown[];
  onChange: (value: unknown[]) => void;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /** Schema for array items - can include $ref */
  itemSchema?: {
    type?: string | undefined;
    description?: string | undefined;
    $ref?: string | undefined;
    properties?: Record<string, unknown> | undefined;
    required?: string[] | undefined;
  } | undefined;
  /** Schema definitions for resolving $ref */
  definitions?: Record<string, unknown> | undefined;
  /** Minimum number of items */
  minItems?: number | undefined;
  /** Maximum number of items */
  maxItems?: number | undefined;
  /** Action outputs for expression evaluation in preview */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since last run */
  isStale?: boolean | undefined;
  /**
   * Editor to show when the value is empty / unset. Existing non-empty
   * values always win (a real array opens in list mode; a raw string
   * or expression opens in JSON mode). Defaults to `'list'`; use
   * `'json'` for params that typically receive a whole-array token
   * reference (e.g. Filter `from`).
   */
  defaultView?: 'list' | 'json' | undefined;
  /**
   * Optional slot rendered inside the array editor's bordered input
   * surface on the right edge. In expression / JSON mode this lives
   * inside the tokenized input wrapper; in list mode it sits next to
   * the mode toggle in the label row so it stays close to the editor
   * controls.
   */
  endActions?: ReactNode | undefined;
}

/**
 * Resolve a $ref to get the actual schema
 */
function resolveRef(
  ref: string | undefined,
  definitions: Record<string, unknown> | undefined
): Record<string, unknown> | null {
  if (!ref || !definitions) return null;

  // Parse ref like "#/definitions/ClientSendAttachment"
  const match = ref.match(/^#\/definitions\/(.+)$/);
  if (!match || !match[1]) return null;

  const defName = match[1];
  const resolved = definitions[defName];
  return resolved && typeof resolved === 'object' ? resolved as Record<string, unknown> : null;
}

/**
 * Get resolved item schema with properties
 */
function getResolvedItemSchema(
  itemSchema: ArrayFieldProps['itemSchema'],
  definitions: Record<string, unknown> | undefined
): ResolvedItemSchema | null {
  if (!itemSchema) {
    return null;
  }

  // Resolve $ref if present
  let schema = itemSchema;
  if (itemSchema.$ref) {
    const resolved = resolveRef(itemSchema.$ref, definitions);
    if (resolved) {
      schema = resolved as typeof itemSchema;
    } else {
      return null;
    }
  }

  // Check if it's an object with properties
  if (schema?.type !== 'object' || !schema.properties) {
    return null;
  }

  const properties: Record<string, ItemPropertySchema> = {};
  for (const [name, propSchema] of Object.entries(schema.properties)) {
    const prop = propSchema as Record<string, unknown>;
    properties[name] = {
      name,
      type: prop.type as string | undefined,
      description: prop.description as string | undefined,
      format: prop.format as string | undefined,
      'x-ms-summary': prop['x-ms-summary'] as string | undefined,
    };
  }

  const result: ResolvedItemSchema = {
    type: 'object',
    properties,
  };
  if (schema.required) {
    result.required = schema.required as string[];
  }
  return result;
}

/**
 * Convert array of objects to ComplexArrayItem[]
 */
function arrayToComplexItems(arr: unknown[], schema: ResolvedItemSchema): ComplexArrayItem[] {
  return arr.map((item) => {
    const values: Record<string, string> = {};
    if (typeof item === 'object' && item !== null) {
      for (const propName of Object.keys(schema.properties)) {
        const val = (item as Record<string, unknown>)[propName];
        values[propName] = val !== undefined
          ? (typeof val === 'string' ? val : JSON.stringify(val))
          : '';
      }
    }
    return {
      id: generateSegmentId(),
      values,
      isExpanded: true,
    };
  });
}

/**
 * Convert ComplexArrayItem[] to array of objects
 */
function complexItemsToArray(items: ComplexArrayItem[], schema: ResolvedItemSchema): unknown[] {
  const result: unknown[] = [];
  for (const item of items) {
    // Check if at least one property has a value
    const hasValue = Object.values(item.values).some((v) => v.trim() !== '');
    if (hasValue) {
      const obj: Record<string, unknown> = {};
      for (const [propName, propSchema] of Object.entries(schema.properties)) {
        const val = item.values[propName] || '';
        if (val.trim()) {
          // Try to parse as JSON for non-string types
          if (propSchema.type !== 'string') {
            try {
              obj[propName] = JSON.parse(val);
            } catch {
              obj[propName] = val;
            }
          } else {
            obj[propName] = val;
          }
        }
      }
      result.push(obj);
    }
  }
  return result;
}

/**
 * Get a summary of the item for the collapsed header
 */
function getItemSummary(item: ComplexArrayItem, schema: ResolvedItemSchema): string {
  const parts: string[] = [];
  // Get first few non-empty values
  for (const propName of Object.keys(schema.properties)) {
    const val = item.values[propName];
    if (val && val.trim()) {
      parts.push(val.trim());
      if (parts.length >= 2) break;
    }
  }
  return parts.length > 0 ? parts.join(', ') : '(empty)';
}

type ArrayFieldValue = string | ValueSegment[] | unknown[];

/** A real array prop — excludes ValueSegment[], which is also an array. */
function isActualArray(value: ArrayFieldValue): value is unknown[] {
  return Array.isArray(value) && !isValueSegmentArray(value);
}

/** String view of a non-array prop, or `null` if the prop is a real array. */
function valueToRawString(value: ArrayFieldValue): string | null {
  if (isValueSegmentArray(value)) return segmentsToString(value);
  if (typeof value === 'string') return value;
  return null;
}

/** Same as `valueToRawString` but coerces the array case to `''`. */
function valueToString(value: ArrayFieldValue): string {
  return valueToRawString(value) ?? '';
}

/**
 * Returns the trimmed expression text when the prop is a runtime
 * expression (`@…` or `${…}`), else `null`. Expressions can't be parsed
 * as JSON arrays, so the caller renders a tokenized input instead.
 */
function getExpressionString(value: ArrayFieldValue): string | null {
  const str = valueToRawString(value);
  if (!str) return null;
  const trimmed = str.trim();
  return trimmed.startsWith('@') || trimmed.startsWith('${') ? trimmed : null;
}

/**
 * Coerce the prop into a parsed array. Handles real arrays plus
 * JSON-string arrays (e.g. `'["a"]'`) emitted by ParameterList's shared
 * `getJsonValue` helper, so the editor opens in list mode rather than
 * the JSON-text fallback. Returns `null` for anything that doesn't
 * cleanly resolve to an array.
 */
function tryParseArrayValue(value: ArrayFieldValue): unknown[] | null {
  if (isActualArray(value)) return value;
  const str = valueToRawString(value);
  if (!str) return null;
  const trimmed = str.trim();
  if (!trimmed.startsWith('[')) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Pretty-print an array for the JSON editor; empty arrays render as ''. */
function formatArrayAsJsonText(arr: unknown[]): string {
  return arr.length > 0 ? JSON.stringify(arr, null, 2) : '';
}

/**
 * Pick the initial editor mode (`true` = list, `false` = JSON) on mount.
 *
 * Non-empty values dictate the view; `defaultView` only applies when the
 * value is effectively unset. Treat `[]` as "unset" too — workflow
 * definitions often persist an empty default array on save and we don't
 * want that to override the author's `defaultView` choice on reopen.
 */
function getInitialIsExpanded(
  value: ArrayFieldValue,
  defaultView: 'list' | 'json',
): boolean {
  const parsed = tryParseArrayValue(value);
  if (parsed && parsed.length > 0) return true;
  if (parsed === null) {
    const raw = valueToRawString(value);
    if (raw && raw.trim()) return false;
  }
  return defaultView !== 'json';
}

/**
 * Convert array to ArrayItem[] for editing
 */
function arrayToItems(arr: unknown[]): ArrayItem[] {
  return arr.map((val) => ({
    id: generateSegmentId(),
    value: typeof val === 'string' ? val : JSON.stringify(val),
  }));
}

/**
 * Convert items to array (only non-empty items)
 */
function itemsToArray(items: ArrayItem[]): unknown[] {
  const result: unknown[] = [];
  for (const item of items) {
    // Only include items with non-empty values
    if (item.value.trim()) {
      // Try to preserve types (numbers, booleans, objects)
      try {
        result.push(JSON.parse(item.value));
      } catch {
        result.push(item.value);
      }
    }
  }
  return result;
}

/**
 * Ensure there's always at least one empty row at the end
 */
function ensureEmptyRow(items: ArrayItem[]): ArrayItem[] {
  const lastItem = items[items.length - 1];
  if (!lastItem || lastItem.value.trim() !== '') {
    return [
      ...items,
      { id: generateSegmentId(), value: '' },
    ];
  }
  return items;
}

/**
 * Check if two arrays are deeply equal (for simple JSON-like arrays)
 */
function arraysEqual(a: unknown[], b: unknown[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export const ArrayField = ({
  name,
  label,
  value,
  onChange,
  description,
  placeholder,
  required,
  disabled = false,
  error,
  itemSchema,
  definitions,
  actionOutputs,
  hasRunData,
  isStale,
  defaultView = 'list',
  endActions,
}: ArrayFieldProps) => {
  const styles = useStyles();

  // Check if the value is a workflow expression (e.g., @body('x'), ${variables('y')}).
  // Expressions can't be parsed as arrays — render a tokenized input editor instead.
  const expressionString = useMemo(() => getExpressionString(value), [value]);

  // Resolve item schema to check if complex
  const resolvedItemSchema = useMemo(
    () => getResolvedItemSchema(itemSchema, definitions),
    [itemSchema, definitions]
  );
  const isComplexItems = resolvedItemSchema !== null;

  // Initial editor mode — see getInitialIsExpanded for the rules.
  const [isExpanded, setIsExpanded] = useState(() =>
    getInitialIsExpanded(value, defaultView),
  );

  // Track focus state for showing preview (shared across both modes)
  const { isFocused, containerHandlers, previewHandlers } = useFieldPreviewFocus();

  // Parse initial value (only on mount - we use internal state for updates)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialArray = useMemo<unknown[]>(() => tryParseArrayValue(value) ?? [], []);

  // Internal items state for simple items - maintains stable IDs across edits
  const [simpleItems, setSimpleItems] = useState<ArrayItem[]>(() =>
    ensureEmptyRow(arrayToItems(initialArray))
  );

  // Internal items state for complex items
  const [complexItems, setComplexItems] = useState<ComplexArrayItem[]>(() =>
    resolvedItemSchema
      ? arrayToComplexItems(initialArray, resolvedItemSchema)
      : []
  );

  // Track if complexItems has been initialized with a valid schema
  const complexItemsInitializedRef = useRef(resolvedItemSchema !== null);

  // Re-initialize complexItems when resolvedItemSchema becomes available for the first time
  useEffect(() => {
    if (resolvedItemSchema && !complexItemsInitializedRef.current) {
      complexItemsInitializedRef.current = true;
      const currentArray = tryParseArrayValue(value) ?? [];
      setComplexItems(arrayToComplexItems(currentArray, resolvedItemSchema));
    }
  }, [resolvedItemSchema, value]);

  // Track if we initiated the change (to avoid re-parsing our own updates)
  const isInternalChangeRef = useRef(false);

  // Track the last array we sent out
  const lastArrayRef = useRef<unknown[]>(initialArray);

  // JSON text for collapsed mode. Pretty-print arrays (real or JSON-string
  // form); preserve raw string (including ${} tokens) for everything else.
  const [jsonText, setJsonText] = useState(() => {
    const parsed = tryParseArrayValue(value);
    return parsed !== null ? formatArrayAsJsonText(parsed) : valueToString(value);
  });
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Sync from external value changes only
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    const externalArray = tryParseArrayValue(value);

    // Non-array values (raw strings, ValueSegment[]) only update jsonText —
    // list rows stay untouched so tokens survive a re-render.
    if (externalArray === null) {
      setJsonText(valueToString(value));
      lastArrayRef.current = [];
      return;
    }

    // Only re-parse if the external value is different from what we last sent
    if (!arraysEqual(externalArray, lastArrayRef.current)) {
      if (isComplexItems && resolvedItemSchema) {
        setComplexItems(arrayToComplexItems(externalArray, resolvedItemSchema));
      } else {
        const newItems = ensureEmptyRow(arrayToItems(externalArray));
        setSimpleItems(newItems);
      }
      setJsonText(formatArrayAsJsonText(externalArray));
      lastArrayRef.current = externalArray;
    }
  }, [value, isComplexItems, resolvedItemSchema]);

  // Computed preview of the current array
  const currentArray = useMemo(() => {
    if (isComplexItems && resolvedItemSchema) {
      return complexItemsToArray(complexItems, resolvedItemSchema);
    }
    return itemsToArray(simpleItems);
  }, [isComplexItems, resolvedItemSchema, complexItems, simpleItems]);

  // Notify parent of changes (simple items)
  const notifySimpleChange = useCallback((newItems: ArrayItem[]) => {
    const arr = itemsToArray(newItems);
    lastArrayRef.current = arr;
    isInternalChangeRef.current = true;
    onChange(arr);
  }, [onChange]);

  // Notify parent of changes (complex items)
  const notifyComplexChange = useCallback((newItems: ComplexArrayItem[]) => {
    if (!resolvedItemSchema) return;
    const arr = complexItemsToArray(newItems, resolvedItemSchema);
    lastArrayRef.current = arr;
    isInternalChangeRef.current = true;
    onChange(arr);
  }, [onChange, resolvedItemSchema]);

  // Handle simple item value change
  const handleSimpleItemChange = useCallback((itemId: string, newValue: string) => {
    setSimpleItems((prev) => {
      const newItems = prev.map((item) =>
        item.id === itemId ? { ...item, value: newValue } : item
      );
      const withEmptyRow = ensureEmptyRow(newItems);
      notifySimpleChange(withEmptyRow);
      return withEmptyRow;
    });
  }, [notifySimpleChange]);

  // Handle complex item property change
  const handleComplexPropertyChange = useCallback((itemId: string, propName: string, newValue: string) => {
    if (!resolvedItemSchema) return;
    setComplexItems((prev) => {
      const newItems = prev.map((item) =>
        item.id === itemId
          ? { ...item, values: { ...item.values, [propName]: newValue } }
          : item
      );
      notifyComplexChange(newItems);
      return newItems;
    });
  }, [notifyComplexChange, resolvedItemSchema]);

  // Toggle complex item expansion
  const toggleComplexItemExpand = useCallback((itemId: string) => {
    setComplexItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, isExpanded: !item.isExpanded } : item
      )
    );
  }, []);

  // Remove simple item
  const handleRemoveSimpleItem = useCallback((itemId: string) => {
    setSimpleItems((prev) => {
      const newItems = prev.filter((item) => item.id !== itemId);
      const withEmptyRow = ensureEmptyRow(newItems);
      notifySimpleChange(withEmptyRow);
      return withEmptyRow;
    });
  }, [notifySimpleChange]);

  // Remove complex item
  const handleRemoveComplexItem = useCallback((itemId: string) => {
    if (!resolvedItemSchema) return;
    setComplexItems((prev) => {
      const newItems = prev.filter((item) => item.id !== itemId);
      notifyComplexChange(newItems);
      return newItems;
    });
  }, [notifyComplexChange, resolvedItemSchema]);

  // Add new complex item
  const handleAddComplexItem = useCallback(() => {
    if (!resolvedItemSchema) return;
    const emptyValues: Record<string, string> = {};
    for (const propName of Object.keys(resolvedItemSchema.properties)) {
      emptyValues[propName] = '';
    }
    setComplexItems((prev) => {
      const newItems = [...prev, { id: generateSegmentId(), values: emptyValues, isExpanded: true }];
      notifyComplexChange(newItems);
      return newItems;
    });
  }, [notifyComplexChange, resolvedItemSchema]);

  // Sync JSON text when switching to collapsed mode
  const handleSwitchToCollapsed = useCallback(() => {
    setJsonText(formatArrayAsJsonText(currentArray));
    setJsonError(null);
    setIsExpanded(false);
  }, [currentArray]);

  // Check if a simple item is empty (for hiding its delete button).
  const isSimpleItemEmpty = (item: ArrayItem) => !item.value.trim();

  // Raw workflow expressions (e.g. `@body('x')`) can't be edited as a
  // structured list — force JSON mode and disable the list-mode toggle.
  const isExpressionValue = expressionString !== null;
  const listMode = isExpanded && !isExpressionValue;

  const labelHeader = (
    <div className={styles.labelRow}>
      <Label className={styles.label} htmlFor={name}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </Label>
      <div className={styles.modeToggle}>
        {endActions && (
          <>
            <span className={styles.endActionsInLabelRow}>{endActions}</span>
            <span className={styles.labelRowSeparator} aria-hidden="true" />
          </>
        )}
        <Tooltip
          content={
            isExpressionValue
              ? `List view isn't available — the value is a workflow expression. Replace it with a literal array (e.g. ["a", "b"]) to edit as a list.`
              : 'Edit as list'
          }
          relationship="label"
        >
          <Button
            appearance={listMode ? 'primary' : 'subtle'}
            size="small"
            icon={<TextFieldFilled />}
            onClick={isExpressionValue ? undefined : () => setIsExpanded(true)}
            disabled={disabled || isExpressionValue}
            aria-label="Edit as list"
            aria-pressed={listMode}
          />
        </Tooltip>
        <Tooltip content="Edit as JSON" relationship="label">
          <Button
            appearance={!listMode ? 'primary' : 'subtle'}
            size="small"
            icon={<CodeFilled />}
            onClick={isExpressionValue ? undefined : handleSwitchToCollapsed}
            disabled={disabled}
            aria-label="Edit as JSON"
            aria-pressed={!listMode}
          />
        </Tooltip>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      {labelHeader}

      {description && (
        <Text className={styles.description}>{description}</Text>
      )}

      {isExpressionValue ? (
        // Expression value - single-line tokenized input (list view is disabled)
        <div className={styles.jsonEditorWrapper} {...containerHandlers}>
          <TokenizedInput
            name={name}
            value={[{
              id: 'expr',
              type: ValueSegmentType.LITERAL,
              value: expressionString,
            }]}
            onChange={(segments) => onChange(segmentsToString(segments) as unknown as unknown[])}
            placeholder={placeholder || 'Enter expression...'}
            disabled={disabled}
            actionOutputs={actionOutputs}
          />
          {isFocused && expressionString.trim() && (
            <ExpressionPreview
              value={expressionString}
              actionOutputs={actionOutputs}
              hasRunData={hasRunData}
              isStale={isStale}
              mode="overlay"
              {...previewHandlers}
            />
          )}
        </div>
      ) : listMode ? (
        // List mode - per-item editors (simple values or complex object forms)
        <div className={styles.itemListWrapper} {...containerHandlers}>
          {isComplexItems && resolvedItemSchema ? (
            // Complex items - object forms. Cards are 1:1 with `complexItems`:
            // Add Item appends, Remove deletes, typing edits in place. No
            // hidden trailing-empty padding — the explicit Add button is the
            // only way to grow the list.
            <div className={styles.objectItemList}>
              {complexItems.length === 0 ? (
                <div className={styles.emptyState}>
                  No items. Click "Add Item" to add one.
                </div>
              ) : (
                complexItems.map((item, index) => (
                    <div key={item.id} className={styles.objectItemCard}>
                      <div
                        className={styles.objectItemHeader}
                        onClick={() => toggleComplexItemExpand(item.id)}
                      >
                        <div className={styles.objectItemHeaderLeft}>
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={item.isExpanded ? <ChevronDownFilled /> : <ChevronRightFilled />}
                            className={styles.expandButton}
                            aria-label={item.isExpanded ? 'Collapse' : 'Expand'}
                            disabled={disabled}
                          />
                          <Text className={styles.objectItemIndex}>Item {index + 1}</Text>
                          {!item.isExpanded && (
                            <Text className={styles.objectItemSummary}>
                              {getItemSummary(item, resolvedItemSchema)}
                            </Text>
                          )}
                        </div>
                        <Tooltip content="Remove item" relationship="label">
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<DeleteFilled />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveComplexItem(item.id);
                            }}
                            disabled={disabled}
                            className={styles.deleteButton}
                            aria-label="Remove item"
                          />
                        </Tooltip>
                      </div>
                      {item.isExpanded && (
                        <div className={styles.objectItemContent}>
                          {Object.entries(resolvedItemSchema.properties).map(([propName, propSchema]) => {
                            const isRequired = resolvedItemSchema.required?.includes(propName);
                            const displayLabel = propSchema['x-ms-summary'] || propName;
                            if (propSchema.type === 'boolean') {
                              return (
                                <BooleanField
                                  key={propName}
                                  name={`${name}_${item.id}_${propName}`}
                                  label={displayLabel}
                                  value={item.values[propName] === 'true'}
                                  onChange={(checked) =>
                                    handleComplexPropertyChange(item.id, propName, String(checked))
                                  }
                                  description={propSchema.description}
                                  required={isRequired}
                                  disabled={disabled}
                                />
                              );
                            }

                            return (
                              <div key={propName} className={styles.objectItemProperty}>
                                <Label className={styles.objectItemPropertyLabel}>
                                  {displayLabel}
                                  {isRequired && (
                                    <span className={styles.objectItemPropertyRequired}>*</span>
                                  )}
                                </Label>
                                <TokenizedInput
                                  name={`${name}_${item.id}_${propName}`}
                                  value={[{
                                    id: `${item.id}_${propName}`,
                                    type: ValueSegmentType.LITERAL,
                                    value: item.values[propName] || '',
                                  }]}
                                  onChange={(segments) =>
                                    handleComplexPropertyChange(item.id, propName, segmentsToString(segments))
                                  }
                                  placeholder={propSchema.description || `Enter ${displayLabel}...`}
                                  disabled={disabled}
                                  actionOutputs={actionOutputs}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))
              )}
              <Button
                appearance="subtle"
                size="small"
                icon={<AddFilled />}
                onClick={handleAddComplexItem}
                disabled={disabled}
                className={styles.addItemButton}
              >
                Add Item
              </Button>
            </div>
          ) : (
            // Simple items - single value inputs
            <div className={styles.itemList}>
              {simpleItems.map((item, index) => (
                <div key={item.id} className={styles.itemRow}>
                  <div className={styles.itemIndex}>{index + 1}</div>
                  <div className={styles.itemInput}>
                    <TokenizedInput
                      name={`${name}_${item.id}`}
                      value={[{
                        id: item.id,
                        type: ValueSegmentType.LITERAL,
                        value: item.value,
                      }]}
                      onChange={(segments) => handleSimpleItemChange(item.id, segmentsToString(segments))}
                      placeholder={placeholder || 'Enter value...'}
                      disabled={disabled}
                      actionOutputs={actionOutputs}
                    />
                  </div>
                  <Tooltip content="Remove item" relationship="label">
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DeleteFilled />}
                      onClick={() => handleRemoveSimpleItem(item.id)}
                      disabled={disabled}
                      className={mergeClasses(styles.deleteButton, isSimpleItemEmpty(item) && styles.deleteButtonHidden)}
                      aria-label="Remove item"
                    />
                  </Tooltip>
                </div>
              ))}
            </div>
          )}
          {/* Preview - shown as overlay when focused and has value */}
          {isFocused && currentArray.length > 0 && (
            <ExpressionPreview
              value={currentArray}
              actionOutputs={actionOutputs}
              hasRunData={hasRunData}
              isStale={isStale}
              mode="overlay"
              {...previewHandlers}
            />
          )}
        </div>
      ) : (
        // Collapsed mode - JSON editor with expression support
        <div className={styles.jsonEditorWrapper} {...containerHandlers}>
          <TokenizedInput
            name={`${name}_json`}
            value={[{
              id: 'json',
              type: ValueSegmentType.LITERAL,
              value: jsonText,
            }]}
            onChange={(segments) => {
              const newText = segmentsToString(segments);
              setJsonText(newText);
              setJsonError(null);

              // Try to parse as JSON first so structurally valid input like
              // ["${foo}"] is preserved as an array. Only fall back to raw
              // passthrough when parsing fails AND the text contains a
              // ${...} token — that's the case where the whole value is a
              // runtime expression whose shape can't be statically validated.
              try {
                const parsed = newText.trim() ? JSON.parse(newText) : [];
                if (!Array.isArray(parsed)) {
                  setJsonError('Value must be a JSON array');
                  return;
                }
                isInternalChangeRef.current = true;
                // Update internal state based on item type
                if (isComplexItems && resolvedItemSchema) {
                  setComplexItems(arrayToComplexItems(parsed, resolvedItemSchema));
                } else {
                  setSimpleItems(ensureEmptyRow(arrayToItems(parsed)));
                }
                onChange(parsed);
                lastArrayRef.current = parsed;
              } catch {
                if (containsTemplateToken(newText)) {
                  isInternalChangeRef.current = true;
                  lastArrayRef.current = [];
                  onChange(newText as unknown as unknown[]);
                  return;
                }
                setJsonError('Invalid JSON');
              }
            }}
            placeholder="[]"
            multiline
            disabled={disabled}
            actionOutputs={actionOutputs}
          />
          {jsonError && (
            <Text className={styles.error}>{jsonError}</Text>
          )}
          {/* Preview - shown as overlay when focused and has value */}
          {isFocused && jsonText.trim() && !jsonError && (
            <ExpressionPreview
              value={currentArray}
              actionOutputs={actionOutputs}
              hasRunData={hasRunData}
              isStale={isStale}
              mode="overlay"
              {...previewHandlers}
            />
          )}
        </div>
      )}

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
};

ArrayField.displayName = 'ArrayField';
